import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listAuthors } from '../services/authorApi';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { formatLocalDate } from '../utils/formatDate';
import './AuthorsPage.css';

const AuthorsPage = () => {
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useDocumentTitle('Authors');

  useEffect(() => {
    setLoading(true);
    setError(null);
    listAuthors({ q: query || undefined, page, size: 12 })
      .then((data) => {
        setAuthors(data.content || []);
        setTotalPages(data.totalPages || 0);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [query, page]);

  const handleSearch = (event) => {
    event.preventDefault();
    setPage(0);
    setQuery(search.trim());
  };

  return (
    <div className="reader-page">
      <div className="container">
        <header className="reader-header authors-header">
          <div className="reader-header-main">
            <p className="reader-kicker">Writer channels</p>
            <h1>Authors</h1>
            <p className="reader-lead">
              Browse researcher-published channels on DarkNews. Follow a channel or subscribe via RSS
              from their profile.
            </p>
            <form className="reader-search" onSubmit={handleSearch}>
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or slug…"
                aria-label="Search authors"
              />
              <button type="submit" className="reader-btn-secondary">
                Search
              </button>
            </form>
          </div>
        </header>

        {loading && <p className="reader-status">Loading authors…</p>}
        {error && <p className="reader-error">{error}</p>}

        {!loading && !error && authors.length === 0 && (
          <p className="reader-empty">No author channels match your search yet.</p>
        )}

        {!loading && !error && authors.length > 0 && (
          <ul className="authors-grid">
            {authors.map((author) => (
              <li key={author.slug} className="author-card">
                <Link to={`/authors/${author.slug}`} className="author-card-link">
                  <h2>{author.displayName}</h2>
                  <p className="author-card-slug">@{author.slug}</p>
                  {author.bioExcerpt && <p className="author-card-bio">{author.bioExcerpt}</p>}
                  <p className="author-card-meta">
                    {author.publishedCount} published
                    {author.latestPublishedAt
                      ? ` · latest ${formatLocalDate(author.latestPublishedAt)}`
                      : ''}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        )}

        {totalPages > 1 && (
          <nav className="reader-pagination" aria-label="Authors pagination">
            <button
              type="button"
              className="reader-btn-secondary"
              disabled={page <= 0}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </button>
            <span className="reader-page-indicator">
              Page {page + 1} of {totalPages}
            </span>
            <button
              type="button"
              className="reader-btn-secondary"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </nav>
        )}
      </div>
    </div>
  );
};

export default AuthorsPage;
